/**
 * DEFUSE S15 — typed property-edit CUTOVER BUILDERS.
 *
 * The pure writer CORE (`applyTypedPropertyEditWithDescriptor` /
 * `applyTypedPropertyEdit` + the field setter / no-op / reject semantics) lives
 * in `typedPropertyWriterCore.ts` (leaf) and is RE-EXPORTED here verbatim, so
 * every existing import path (`./typedPropertyWriter`, the package barrel) keeps
 * resolving unchanged — the file-split pattern (`descriptors/types.ts` /
 * `geometryEntityTypes.ts`) mandated by the size cap, with the builders→core
 * import edge pointing at the leaf (zero dep cycles, ПРИОРИТЕТ #2).
 *
 * This module owns the S15 LIVE-EDIT-PATH builders the geometryReducer
 * `properties/apply` steps call — one builder per cutover slice:
 *   - Step 1  — `buildTypedSimpleGeometryEdit`   (Line/Circle/Arc geometry)
 *   - Step 1b — `buildTypedCommonEdit`           (6 non-Color common props)
 *   - Step 1c — `buildTypedColorEdit`            (Color union + decideColorEdit)
 *   - Step 1d — `buildTypedExtendedGeometryEdit` (Slice A: Rectangular
 *                Associative Array + LWPOLYLINE special geometry — the first
 *                branch-DELETING extended slice; the legacy `propertyTranslator`
 *                per-kind branches for these two kinds are gone. Slice B:
 *                Point + Ellipse — ADDITIVE first-time writability; these kinds
 *                never had a legacy write path, so there is no branch to delete
 *                and no double-write risk).
 */

import type { EntityType } from "@dxf-ai-editor/contracts";
import type {
  DrawingEntity,
  EntityColor,
  ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  coerceColor,
  colorToLegacyString,
  semanticColorEqual,
  serializeColor,
} from "@dxf-ai-editor/drawing-model";

import type { TypedPropertyDescriptor } from "./descriptors/types";
import { resolveReadOnly } from "./descriptors/types";
import { isCommonObjectProperty } from "./propertyCapabilities";
import {
  getTypedPropertyDescriptor,
  getTypedPropertyDescriptors,
  resolveDisplay,
} from "./typedPropertyDescriptors";
import {
  applyTypedPropertyEditWithDescriptor,
  isTypedFieldEditNoop,
  parseTypedPropertyInput,
  rawEqual,
  typedFieldPatchTopField,
  writeTypedField,
  type TypedPropertyEditResult,
} from "./typedPropertyWriterCore";
import {
  applyTypedSideEffect,
  isSideEffectMutatorRegistered,
  labelCanonicalDirectiveIsNoop,
  sideEffectCanonicalizes,
  sideEffectValueRejects,
  LABEL_CANONICAL_MUTATORS,
} from "./sideEffectMutators";
import { isControlViewport } from "./viewportMutateEntity";

// ── Re-export the writer CORE so existing import paths stay unchanged ─────────
export {
  applyTypedPropertyEdit,
  applyTypedPropertyEditWithDescriptor,
  isTypedFieldEditNoop,
  parseTypedPropertyInput,
  rawEqual,
  typedFieldPatchTopField,
  writeTypedField,
  type TypedPropertyEditResult,
  type TypedPropertyEditStatus,
  type TypedPropertySideEffect,
} from "./typedPropertyWriterCore";

/** W2.1 (S16-ARRAYRECT-READONLY-ALIGN) — placeholder entity `resolveReadOnly`
 *  can safely receive from a shell-only context (no live typed entity): every
 *  currently-registered `constraints.readOnly` is a plain `boolean`, so the
 *  resolver never actually reads a field off it. */
const EMPTY_SHELL_PROBE = {} as DrawingEntity;

// ── S15 cutover helper: simple-geometry (Line/Circle/Arc) typed document edit ──

/** A single-entity typed field patch for `patchEntityById` (shallow-merges the
 *  changed top-level fields onto the entity, id/spaceId preserved). */
export type TypedFieldPatch = {
  entityId: string;
  changes: Partial<DrawingEntity>;
};

/** The kinds whose geometry the S15 typed writer OWNS in the live edit path
 *  (Line/Circle/Arc — where the legacy `applySimpleGeometryPropertyChanges` +
 *  reconcile scaffolds are being deleted). Point/Ellipse land via the
 *  EXTENDED-geometry writer instead (Slice B, {@link
 *  TYPED_EXTENDED_GEOMETRY_KINDS}): they have no legacy typed applier to prove
 *  parity against, so their gate is a hand-authored DXF-group-code SPEC oracle
 *  (`extGeomSliceB.parity.test.ts`), not the legacy-parity oracle. */
const TYPED_SIMPLE_GEOMETRY_KINDS = new Set<EntityType>(["Line", "Circle", "Arc"]);

/**
 * ONE-TRUTH driver behind the S15 cutover builders: apply a sequence of
 * descriptor-resolved typed edits to `documentEntity`, collecting each APPLIED
 * edit's changed TOP-LEVEL field into a single `patchEntityById` shallow-merge
 * patch (untouched axes keep the document's full precision by construction).
 * `resolve` returns the descriptor for a change (or `undefined` to SKIP it — a
 * non-cutover label, a read-only calc, or a label with no typed home); a
 * `rejected` / `noop` status leaves the entity untouched. Returns `null` when
 * nothing was applied (a pure no-op / all-skipped apply).
 *
 * The touched top field is `typedFieldPatchTopField(path)` — the first path
 * segment with any array-map `[]` suffix stripped (Blocker-A: a bare
 * `path.split(".")[0]` turned the LWPOLYLINE Bulges path `vertices[].bulge` into
 * the bogus record key `"vertices[]"`, silently dropping the applied edit).
 *
 * N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 3) — a descriptor with
 * a FUNCTION-valued (entity-state) `readOnly` may ALSO declare
 * `readOnlyFromChanges`: given the FULL `changes` batch, it may return a
 * PARTIAL entity override reflecting a SIBLING change elsewhere in this SAME
 * apply that is not itself typed-writer-owned (e.g. Hatch "Pattern type", an
 * `enum` applied by a LATER, separate step this accumulator never sees). When
 * present, the `readOnly` gate (inside `applyTypedPropertyEditWithDescriptor`)
 * is evaluated against `{ ...entity, ...override }` instead of the bare
 * accumulator, so a same-apply "Pattern type" + "Double" submission resolves
 * against the BATCH'S final intent, not an order-dependent stale field.
 * SAFETY: the override is used ONLY for that one `applyTypedPropertyEditWith-
 * Descriptor` call — the result is merged back onto the REAL accumulator ONE
 * FIELD AT A TIME (`typedFieldPatchTopField`, the SAME key the emitted patch
 * uses), never wholesale, so the override's extra field (`patternType`) can
 * NEVER leak into the accumulator or the patch. Absent `readOnlyFromChanges`
 * (the overwhelming majority of descriptors), `evalEntity === entity` by
 * reference — zero behaviour change.
 */
function buildTypedFieldPatch(
  documentEntity: DrawingEntity,
  changes: ReadonlyArray<{ name: string; value: string }>,
  resolve: (
    entity: DrawingEntity,
    change: { name: string; value: string },
  ) => TypedPropertyDescriptor | undefined,
  // Optional per-edit override — when it returns a result, that result is used
  // INSTEAD of the default `applyTypedPropertyEditWithDescriptor` (else fall
  // through to the default). Generic hook; the Color builder uses it for the
  // sentinel-clear bypass. `undefined` ⇒ default writer semantics for that edit.
  applyOverride?: (
    entity: DrawingEntity,
    descriptor: TypedPropertyDescriptor,
    change: { name: string; value: string },
  ) => TypedPropertyEditResult | undefined,
): TypedFieldPatch | null {
  let entity = documentEntity;
  const touchedTopFields = new Set<string>();
  for (const change of changes) {
    const descriptor = resolve(entity, change);
    if (!descriptor?.typedFieldRef) continue;
    const readOnlyOverride =
      typeof descriptor.constraints?.readOnly === "function"
        ? descriptor.constraints.readOnlyFromChanges?.(changes, entity)
        : undefined;
    const evalEntity = readOnlyOverride
      ? ({ ...entity, ...readOnlyOverride } as DrawingEntity)
      : entity;
    const result =
      applyOverride?.(evalEntity, descriptor, change) ??
      applyTypedPropertyEditWithDescriptor(evalEntity, descriptor, change.value);
    if (result.status !== "applied") continue;
    const topField = typedFieldPatchTopField(descriptor.typedFieldRef.path);
    entity = {
      ...entity,
      [topField]: (result.entity as unknown as Record<string, unknown>)[topField],
    } as DrawingEntity;
    touchedTopFields.add(topField);
  }
  if (touchedTopFields.size === 0) return null;
  const patch: Record<string, unknown> = {};
  for (const field of touchedTopFields) {
    patch[field] = (entity as unknown as Record<string, unknown>)[field];
  }
  return { entityId: documentEntity.id, changes: patch as Partial<DrawingEntity> };
}

/**
 * Build the typed geometry patch for a Line/Circle/Arc `properties/apply` by
 * editing the LIVE document entity through the typed writer — the S15 replacement
 * for `applySimpleGeometryPropertyChanges(previewEntity).patch` +
 * `reconcileLinePatchWithDocument` / `reconcileCircleArcPatchWithDocument`.
 *
 * `changes` MUST be the entity-SPECIFIC (non-common) changes — common props keep
 * flowing through the extended-entity translator, so this must never touch them.
 * Each accepted edit's changed TOP-LEVEL field (`start`/`end`/`center`/`radius`/
 * `startAngle`/`endAngle`) is collected into a `patchEntityById` shallow-merge
 * patch; untouched axes carry the document's full precision by construction.
 * Returns `null` for a non-simple kind OR when nothing changed (a no-op apply).
 */
export function buildTypedSimpleGeometryEdit(
  documentEntity: DrawingEntity,
  changes: ReadonlyArray<{ name: string; value: string }>,
): TypedFieldPatch | null {
  if (!TYPED_SIMPLE_GEOMETRY_KINDS.has(documentEntity.type as EntityType)) {
    return null;
  }
  // Only the special geometry descriptors are relevant here; a non-descriptor
  // label (or a read-only calc like Length/Angle) resolves + rejects/no-ops.
  return buildTypedFieldPatch(documentEntity, changes, (entity, change) =>
    getTypedPropertyDescriptor(entity.type as EntityType, change.name),
  );
}

// ── S15 cutover helper: common object-property typed document edit ────────────

/**
 * The NON-Color common object-property labels the S15 typed writer OWNS in the
 * live edit path (Layer / Linetype / Lineweight / Transparency / Linetype Scale
 * / Thickness). These stop flowing through the legacy string-bag common merge
 * (`mergeCommonFieldsForType` in apps/web `propertyTranslator`) and land via
 * `buildTypedCommonEdit` instead.
 *
 * `Color` is DELIBERATELY EXCLUDED — it stays on the legacy path until its own
 * slice: its typed home is the `EntityColor` union with the lossy-vs-lossless
 * suppression (rich colour redisplay) + multi-select semantics the geometryReducer
 * applies, which are a separate Cat-C cutover. So this set is the 6 fields, never 7.
 */
export const TYPED_COMMON_CUTOVER_LABELS: ReadonlySet<string> = new Set<string>([
  "Layer",
  "Linetype",
  "Lineweight",
  "Transparency",
  "Linetype Scale",
  "Thickness",
]);

/**
 * Build the typed COMMON-property patch for a `properties/apply` by editing the
 * LIVE document entity through the typed writer — the S15 replacement for the 6
 * non-Color common fields' legacy `mergeCommonFieldsForType` dual-write. Kind-
 * agnostic sibling of `buildTypedSimpleGeometryEdit`: it reuses the SAME
 * `applyTypedPropertyEditWithDescriptor` core (no-op by display-value equivalence,
 * read-only reject, raw-precision preserve) via the kind's common descriptors
 * (`buildCommonDescriptors`), so a capability-excluded common prop (the descriptor
 * is read-only-calc there — Linetype on MText, Thickness on Dimension/RevCloud)
 * REJECTS exactly where the legacy path skipped, and a display-equivalent
 * re-submit no-ops (preserving stored precision / an absent optional field) by
 * construction.
 *
 * `changes` MUST be the common cutover changes only (Color + geometry-specific
 * stay on the legacy translator). Each accepted edit's changed top-level field
 * (`layer` / `linetype` / `lineweight` / `transparency` / `linetypeScale` /
 * `thickness`) is collected into a `patchEntityById` shallow-merge patch. Returns
 * `null` for an all-no-op / all-rejected apply (nothing to write).
 */
export function buildTypedCommonEdit(
  documentEntity: DrawingEntity,
  changes: ReadonlyArray<{ name: string; value: string }>,
): TypedFieldPatch | null {
  return buildTypedFieldPatch(documentEntity, changes, (entity, change) => {
    // Color is DEFERRED to its own slice — never route it through this writer,
    // even if a caller mistakenly includes it in the cutover change set. A
    // non-cutover label resolves to `undefined` → skipped. A capability-excluded
    // common prop (read-only-calc / ui-projection-deferred descriptor) resolves
    // + REJECTS, matching the legacy `mergeCommonFieldsForType` skip; a display-
    // equivalent re-submit no-ops, preserving stored precision / an absent field.
    if (change.name === "Color" || !TYPED_COMMON_CUTOVER_LABELS.has(change.name)) {
      return undefined;
    }
    return getTypedPropertyDescriptor(entity.type as EntityType, change.name);
  });
}

// ── S15 cutover helper: Color common object-property typed document edit ───────

/**
 * The S15 COLOUR-EDIT DECISION PROCEDURE (@codex P2 ×3 on d1f96758 — replaces the
 * former sentinel-clear bypass). Colour needs its OWN apply/no-op logic because
 * the generic writer compares DISPLAY STRINGS, and the colour display is LOSSY
 * (`colorToLegacyString` drops the 62/430 siblings of a rich union) and NON-
 * CANONICAL (`#FF8000` / `ff8000` / `255,128,0` all denote the display `#ff8000`).
 *
 *   1. `rawEqual(current, target)`            → no-op (trivial identity — also the
 *      old suppression block's LOSSLESS `!EC:` serialize-compare branch).
 *   2. NOT rich && `semanticColorEqual`       → no-op, PRESERVE the RICHER stored
 *      form. Every lossy input form normalizes through the codec first, so
 *      `#FF8000`/`ff8000`/`255,128,0` on a stored rgb+aci all no-op (P2-2), and a
 *      sole re-submit of the entity's own `#ff8000` preserves the union (the #447
 *      win). Gated to NON-rich targets: a lossless `!EC:` write that differs
 *      structurally (e.g. changes only the 62 sibling) is a REAL edit and falls
 *      through to apply, exactly like the old block's serialize-compare.
 *   3. semantically different:
 *      a. SOLE Color edit                     → APPLY. An explicit single-property
 *         colour command is always deliberate: `Color=ByLayer` clears a fallback-
 *         less bookColor, and `Color=3` on a bookColor with fallback 3 clears the
 *         430 book metadata to the plain ACI (P2-3) — a rich colour is never
 *         impossible to clear.
 *      b. MULTI-field draft: NOT rich && `display(target) === display(current)` →
 *         no-op — the incoming IS the lossy redisplay the palette itself emits for
 *         the unchanged colour (the grid re-dispatches its FULL draft), so an
 *         unrelated edit (Layer) must not clobber the stored colour: a fallback-
 *         less bookColor's "ByLayer" redisplay is preserved (P2-1). Otherwise →
 *         APPLY (a genuine colour change inside a multi-field draft).
 *
 * Fidelity to the DELETED geometryReducer suppression block: in MULTI mode this is
 * BYTE-COMPATIBLE — (1) == its rich serialize-compare, (3b) == its lossy display-
 * normalize compare — with ONE intended difference: step (2) also no-ops a
 * lossless rgb write that denotes the identical 420 colour and contradicts no
 * stored sibling (preserving the strictly-richer stored form; the old block
 * applied it). In SOLE mode it FIXES the old path's known edge: the old block
 * always-applied sole edits, clobbering a rich union re-fed its own lossy display;
 * the semantic compare (2) preserves it, while (3a) keeps every deliberate clear
 * applying. `rich` is detected from the DECODED union exactly as the old block did
 * (`serializeColor(target) !== colorToLegacyString(target)`), never from the raw
 * string.
 */
function decideColorEdit(
  entity: DrawingEntity,
  descriptor: TypedPropertyDescriptor,
  change: { name: string; value: string },
  isSoleColorEdit: boolean,
): TypedPropertyEditResult | undefined {
  if (
    descriptor.editorKind !== "color" ||
    descriptor.channel !== "editable-typed-field" ||
    resolveReadOnly(descriptor, entity) ||
    !descriptor.typedFieldRef ||
    !descriptor.toRaw
  ) {
    // non-colour / read-only descriptor — fall through to the generic writer
    // (which rejects a read-only write), never decide here.
    return undefined;
  }
  const target = descriptor.toRaw(change.value, entity) as EntityColor; // = deserializeColor
  const current = coerceColor(descriptor.typedFieldRef.read(entity));

  // 1. trivial identity (covers the lossless `!EC:` no-op — undo-inverse /
  //    MATCHPROP re-submit of the exact stored union).
  if (rawEqual(current, target)) return { entity, status: "noop" };

  // rich = the decoded union is NOT fully representable by its legacy string —
  // the old suppression block's own discriminator, computed from the DECODED
  // colour (never the raw string).
  const targetIsRichLossless =
    serializeColor(target) !== colorToLegacyString(target);

  // 2. semantic no-op — the target denotes the SAME colour, PRESERVE the richer
  //    stored form (codec-normalized: #FF8000/ff8000/255,128,0 ≡ #ff8000).
  if (!targetIsRichLossless && semanticColorEqual(current, target)) {
    return { entity, status: "noop" };
  }

  // 3. semantically different.
  if (!isSoleColorEdit) {
    // 3b. multi-field draft: the palette re-dispatches the UNCHANGED colour as
    //     its lossy display string — suppress exactly like the old block's
    //     display-normalize compare (rich targets never suppress here).
    if (
      !targetIsRichLossless &&
      colorToLegacyString(target) === colorToLegacyString(current)
    ) {
      return { entity, status: "noop" };
    }
  }
  // 3a. sole (deliberate) edit, or a genuine multi-field colour change → APPLY.
  return {
    entity: writeTypedField(entity, descriptor.typedFieldRef.path, target),
    status: "applied",
  };
}

/**
 * Build the typed COLOR patch for a `properties/apply` by editing the LIVE
 * document entity through the typed writer — the S15 replacement for `Color`'s
 * legacy string-bag `mergeCommonFieldsForType` write. A DEDICATED sibling of
 * {@link buildTypedCommonEdit} (Color is deliberately NOT in
 * {@link TYPED_COMMON_CUTOVER_LABELS} and the common writer's `change.name ===
 * "Color"` guard SKIPS it): a set-only add to the cutover set would make the
 * reducer's Step-1b filter pass Color to `buildTypedCommonEdit`, where the guard
 * returns `undefined` → a silent no-op. Keeping the two writers separate holds
 * the "6 never 7" common-cutover invariant (parity sanity assert) while giving
 * Color its own live write route.
 *
 * The apply/no-op decision is {@link decideColorEdit} (see its contract — the
 * full sole-vs-multi × lossy-vs-rich matrix), NOT the generic display-string
 * writer: the colour display is lossy and non-canonical, so string equality
 * neither preserves a rich union nor lets an explicit same-display clear apply.
 *
 * `opts.isSoleColorEdit` is REQUIRED (compile-time forcing — a forgotten flag
 * would silently resurrect either the incidental clobber or the un-clearable
 * bookColor): TRUE iff the apply's per-entity EFFECTIVE change set contains ONLY
 * Color (the exact `hasOtherChanges === false` context the deleted suppression
 * block read); the reducer's Step 1c computes it from the same change set.
 *
 * `changes` MUST be the Color changes only (all other common + geometry-specific
 * changes route through their own writer / the legacy translator). The single
 * touched top-level field is `color`. Returns `null` for an all-no-op / all-
 * rejected apply (nothing to write).
 */
export function buildTypedColorEdit(
  documentEntity: DrawingEntity,
  changes: ReadonlyArray<{ name: string; value: string }>,
  opts: { isSoleColorEdit: boolean },
): TypedFieldPatch | null {
  return buildTypedFieldPatch(
    documentEntity,
    changes,
    (entity, change) =>
      change.name === "Color"
        ? getTypedPropertyDescriptor(entity.type as EntityType, "Color")
        : undefined,
    (entity, descriptor, change) =>
      decideColorEdit(entity, descriptor, change, opts.isSoleColorEdit),
  );
}

// ── S15 Slice A: extended-geometry (ArrayRect / LWPOLYLINE) typed document edit ─

/**
 * The kinds whose SPECIAL (non-common) geometry the S15 typed writer OWNS in the
 * live edit path as of Slice A/B:
 *   - Slice A (branch-DELETING) — the two pure-leaf `propertyTranslator`
 *     branches that were DELETED with the cutover:
 *     - "Rectangular Associative Array" — Rows / Columns / Row spacing / Column
 *       spacing / Base X / Base Y / Axis angle / Associative;
 *     - "LWPOLYLINE" — Vertices / Closed / Bulges (the deleted branch set) PLUS
 *       Constant width / Elevation / Linetype generation, which the legacy branch
 *       silently DROPPED (its early `return` never reached the generic tail while
 *       `labelToFieldByType` has no LWPOLYLINE entries) — a shell↔document
 *       divergence this cutover FIXES: every editable typed descriptor now persists.
 *   - Slice B (ADDITIVE, first-time writability — NO legacy branch to delete):
 *     - "Point" — Position X / Y / Z (groups 10/20/30) + Rotation (group 50,
 *       degrees display ↔ radians raw);
 *     - "Ellipse" — Center X / Y (10/20), Major axis X / Y (the 11/21 endpoint
 *       VECTOR relative to center), Ratio (40, AutoCAD bound (0, 1]),
 *       Start / End parameter (41/42, radians).
 *     Before Slice B these labels had NO write path at all: absent from
 *     `patchablePropertyLookup` + `labelToFieldByType`, the translator's generic
 *     tail dropped them, and the shell `canApplyProperty` gate skipped them —
 *     the Properties rows LOOKED editable but silently no-opped. Grip drag for
 *     both kinds is now ALSO wired (Point: S12b; Ellipse: GRIPS-DRAG-MORE-TYPES,
 *     apps/web `gripDragTranslate.ts`) — Properties-panel edit and grip drag
 *     route through these same typed-writer-owned labels in lockstep.
 *   - Slice C (branch-DELETING for the parity leaves, ADDITIVE for the rest) —
 *     the Text / MText / Dimension SCALAR/STYLE tail. The generic
 *     `labelToFieldByType` WRITE application for these kinds' owned leaves is
 *     DELETED (an owned-label skip-guard in `translateExtendedEntityProperty-
 *     Changes`), while the MAP ITSELF is RETAINED — it stays the shell numeric
 *     validator's one truth (`classifyNumericLabel` / `numericFieldsByType`).
 *     Parity leaves (also in the legacy map): Text Contents/Text style/Height/
 *     Rotation; MText Contents/Text style/Height(nominalHeight)/Width(reference-
 *     Width)/Rotation; Dimension style/Text override/Text height/Text rotation/
 *     Oblique angle/Leader length. ADDITIVE leaves (never had a legacy write —
 *     silent no-ops before): Text Width factor/Oblique angle; MText Insertion
 *     coords; Dimension Angle. Text's Insertion X/Y is ALSO additive-first-write
 *     but is a SIDE-EFFECT leaf, not a plain one (GRIPS-DRAG-MORE-TYPES — routed
 *     through `textInsertionMoveMutator`, {@link getTypedExtendedGeometrySide-
 *     EffectLabels}, so a whole-entity insertion move shifts `alignmentPoint` in
 *     lockstep when present — see that mutator's doc for why the coupling lives
 *     there instead of on independently-owned Alignment X/Y). MText "Background
 *     fill" is a SIDE-EFFECT / two-field label (backgroundFill boolean +
 *     backgroundFillSetting export int, with import/create factories on different
 *     vocabularies) — it is DEFERRED to Slice E, see {@link
 *     EXTENDED_GEOMETRY_SIDE_EFFECT_DEFERRED}. The Dimension Extension 1/2 X/Y +
 *     Text Alignment X/Y OPTIONAL-base point-coords stay EXCLUDED (review BLOCK
 *     P1 — a single-axis edit of an ABSENT optional base persists a
 *     schema-invalid partial point); they revert to their prior silent no-op, see
 *     {@link getTypedExtendedGeometryOwnedLabels} +
 *     EXTENDED_GEOMETRY_OPTIONAL_BASE_POINT_COORDS. Rotation stays VERBATIM
 *     DEGREES (group 50) — the descriptor
 *     is identity (no radians toRaw), matching the legacy `parseFiniteNumeric`
 *     identity. Multileader is DEFERRED to its own predecessor slice: its
 *     apps/web IMPORT preview factory (`dxfGeometry.buildMultileaderPreview`)
 *     emits 13/14 scalar/style leaves as `MLENT_*` renderer-internal keys, not
 *     editable label rows, so they cannot pass the both-factories bag-key census
 *     without a Cat-C preview+renderer reconciliation first. Enum leaves
 *     (MText Attachment/Direction; all Multileader enums) + MText "Line spacing"
 *     are DEFERRED — see {@link getTypedExtendedGeometryOwnedLabels}.
 * Kind literals are the exact `entityTypeSchema` options the descriptor registry
 * is keyed by. Later slices grow this set further (Spline/Multileader = Slice D+).
 */
export const TYPED_EXTENDED_GEOMETRY_KINDS: ReadonlySet<EntityType> =
  new Set<EntityType>([
    "Rectangular Associative Array",
    "LWPOLYLINE",
    "Point",
    "Ellipse",
    "Text",
    "MText",
    "Dimension",
    // S15 Slice E — HATCH. Owned PLAIN leaves (registry-derived): Scale / Angle /
    // Associative (byte-parity with the deleted translator branch). Owned
    // SIDE-EFFECT leaf: Pattern (the ONLY one), routed through
    // `hatchPatternMutator` (see {@link getTypedExtendedGeometrySideEffectLabels}).
    // READ-ONLY (not owned): Solid — a DERIVED display of the SOLID-pattern state
    // (@codex P2 Solid cluster: legacy had no Solid write, AutoCAD/ezdxf couple it
    // to the pattern, the scene adapter ORs `patternName === "SOLID"`), Gradient
    // (structural, mutator-cleared). DEFERRED: Hatch style / Pattern type
    // (label-decode ENUMs — need the enum-rule refinement + a `buildHatchPreview`
    // import-factory reconciliation) + Elevation (import-only, no advertised edit
    // path — see {@link EXTENDED_GEOMETRY_CUTOVER_DEFERRED}). Cat-C follow-ups (E2).
    "Hatch",
    // S15 Slice D — SPLINE (рычаг №1; first CONSUMER of the Slice-E side-effect
    // infra beyond Hatch). Owned PLAIN leaves (registry-derived): Control points /
    // Knots / Weights / Rational (byte-parity with the deleted branch's fit/cp
    // block). Owned SIDE-EFFECT leaf: Fit points (the ONLY one) — routed through
    // `splineFitRecomputeMutator` (fit→control recompute so the rendered curve,
    // built from controlPoints, actually reshapes — #317). Control points / Fit
    // points are count-UNLOCKED descriptors (a fit-recompute / undo changes the
    // point count). READ-ONLY-calc (NOT owned): Degree / Closed — the NURBS
    // well-formedness invariant (knots.length === controlPoints.length + degree +
    // 1) couples degree to the knot vector, so a bare scalar write would VANISH
    // the curve + export an ezdxf-rejected SPLINE; a real degree-elevation /
    // close-spline edit needs a NURBS rebuild slice (future). Length is a calc.
    "Spline",
    // S15 Slice F — VIEWPORT (рычаг №1; ADDITIVE first-time panel writability — no
    // legacy translator branch existed, so no branch to delete + no double-write).
    // Owned PLAIN leaves (registry-derived): Center X/Y (group 10/20) / Width
    // (group 40, exclusiveMin:0) / Height (group 41, exclusiveMin:0) / View twist
    // (group 51) — the paper-space rectangle + view-twist plain field sets. Owned
    // SIDE-EFFECT leaves: Scale (routed through `viewportScaleMutator`) + Display
    // lock / View height / View center X/Y (routed through `viewportMutator`) —
    // each COUPLED (Scale ↔ viewHeight ↔ vpOverrideScale ↔ VP_OVERRIDES XDATA ↔
    // cannoscale; Display lock ↔ status-bit 0x4000 ↔ XDATA; View pan/zoom ↔ derived
    // scale), reproducing the dedicated `applyViewport*` commands VERBATIM via the
    // shared `viewportMutateEntity` helpers (one truth). DEFERRED (not owned): Snap
    // / Grid / UCS icon are `ui-projection-deferred` (no typed home — a viewport-
    // special MATCHPROP subsystem follow-up). Clip / membership stay reader-only
    // side-channels (EPIC-VIEWPORT-CLIP; no panel write path).
    "Viewport",
    // S15 Slice G — BLOCK INSERT (the FINAL extended-geometry cutover; PARTIAL —
    // only the INSERT TRANSFORM SCALARS move to the typed writer). Owned PLAIN
    // leaves (registry-derived): Insertion X / Y (group 10/20, XY_PRESERVE — the
    // untouched axis AND group-30 Z keep the document's full raw precision by
    // construction), Scale X / Y / Z (group 41/42/43, verbatim finite number),
    // Rotation (group 50, DEGREES verbatim), Column / Row count (MINSERT group
    // 70/71, int round + min:1 — an out-of-range count REJECTS per the writer's
    // uniform contract, the SAME documented Slice-A ArrayRect divergence from the
    // deleted branch's `Math.max(1, …)` clamp), Column / Row spacing (group 44/45,
    // verbatim finite number). These 10 land via `buildTypedExtendedGeometryEdit`;
    // the DELETED legacy `propertyTranslator` Block branch owned them before. NOT
    // cut over this slice (retained on the legacy translator branch): the dynamic-
    // block channels Param (`customProperties`) + Visibility (`visibilityState`) —
    // behavior-backed, not plain INSERT fields. "Name" (blockName) is DEFERRED (see
    // {@link EXTENDED_GEOMETRY_CUTOVER_DEFERRED}) — advertised-writable but the
    // deleted branch silently no-oped it, and routing it needs target-block-def
    // validation (a bare set dangles the reference). "Hyperlink" stays NOT-YET-
    // ROUTED — its `blockHyperlinkMutator` is an UNREGISTERED side-effect mutator
    // (empty typed-field path), so it owns no side-effect label by construction
    // (the unregistered-mutator gate) and the legacy path keeps it. The read-only
    // block-record metadata rows (Description / Block Unit / …) stay read-only-calc.
    "Block",
    // LEGACY-POLYLINE/TABLE/OLE ROUTING (@codex F1, P1) — the three kinds added
    // with the heavy `POLYLINE` / `ACAD_TABLE` / `OLE2FRAME` import. Their
    // descriptors (`descriptors/legacyPolylineTableOle.ts`) declare REAL
    // `editable-typed-field` leaves, and both preview factories advertise those
    // rows in `editableProperties`, but NO writer honoured them: this set did not
    // carry the kinds (so Step 1d resolved nothing) and the legacy translator has
    // neither a per-kind branch nor a `labelToFieldByType` entry — so the shell bag
    // took the edit (generic `applyChangesToEntityBag` tail) while the typed
    // document, the DXF export and undo never saw it. ADDITIVE first-time
    // writability, exactly like Slice B (Point / Ellipse): there is no legacy branch
    // to delete, so no double-write is possible.
    //
    // OWNED (registry-derived, all plain leaves — no side-effect mutator on any of
    // the three kinds):
    //   - Polyline: Closed / Linetype generation (the two group-70 BITS, both
    //     writing the whole `flags` integer through the descriptor's fold-back
    //     `toRaw`, so the other six bits survive), Start / End segment width
    //     (groups 40/41 — the entity-level DEFAULTS), Elevation (the group-30 Z of
    //     the 10/20/30 dummy point);
    //   - Table: Position X/Y/Z (group 10/20/30 insertion point — moving a table is
    //     the same edit MOVE / COPY already perform);
    //   - OLE Frame: Upper left / Lower right X/Y (groups 10/20 + 11/21 — the frame
    //     IS the entity's geometry, and AutoCAD's corner grips edit exactly this).
    // Everything else on these kinds is `read-only-calc` / `ui-projection-deferred`
    // at the descriptor level (vertex list, mesh grid, cell structure, table
    // rotation VECTOR, the OLE record descriptors), so ownership excludes them by
    // construction — see the descriptor module's "what is editable here" doc.
    "Polyline",
    "Table",
    "OLE Frame",
  ]);

/**
 * DEFUSE S15 Slice C — labels DEFERRED from the typed extended-geometry cutover
 * even though they resolve to an editable non-enum leaf descriptor. Each is a
 * documented naming divergence the apps/web IMPORT-path preview factory
 * (`dxfGeometry.buildXxxPreview`) has NOT yet migrated to the descriptor label
 * (S16 RESIDUAL territory). Routing them would (a) be a DEAD owned label — the
 * live panel dispatches a DIFFERENT bag key, so no edit ever arrives under the
 * descriptor label — and (b) FAIL the both-factories bag-key census (the import
 * factory emits the other key, never this one). Leaving them on the legacy path
 * is a pure no-op on today's behaviour: the descriptor-label edit is
 * non-functional today too (neither key aligns across all sites).
 *   - MText "Line spacing": the import factory emits "Line spacing factor"
 *     (dxfGeometry.buildMTextPreview); the descriptor / capability roster /
 *     command-engine create factory / `labelToFieldByType` all say "Line
 *     spacing". Pinned in the descriptor census RESIDUAL.MText — S16 unifies it.
 */
const EXTENDED_GEOMETRY_CUTOVER_DEFERRED: Record<string, ReadonlySet<string>> = {
  MText: new Set<string>(["Line spacing"]),
  // S15 Slice E (@codex P3) — HATCH "Elevation" is emitted by the apps/web IMPORT
  // factory (dxfGeometry.buildHatchPreview) but NOT by the command-engine CREATE
  // factory (deriveHatchPreviewProperties / hatchPreviewProperties), and the
  // DELETED legacy HATCH branch had NO Elevation case (it was a silent no-op). It
  // is not advertised editable in either factory (absent from hatchPreviewProperties
  // + copyableInMatchpropSpecial). Owning it as owned-plain would be an ADDITIVE,
  // both-factory-asymmetric first-time write with no advertised edit path — so it
  // is DEFERRED (reverts to the exact prior no-op) until a real symmetric hatch-
  // elevation edit path advertises it in BOTH factories (E2 / a dedicated slice).
  Hatch: new Set<string>(["Elevation"]),
  // S15 Slice G — BLOCK "Name" (blockName). It IS advertised-writable
  // (`blockInsertPatchableProperties`; NOT in `blockInsertReadOnlyProperties`),
  // BUT the DELETED legacy translator Block branch had NO "Name" case — Name fell
  // through to `otherChanges` → `mergeCommonFieldsForType`, which handles only
  // COMMON fields (Layer/Color/…) and DROPS blockName (a silent no-op). Routing it
  // as an owned-plain leaf would make block-rename FIRST-TIME writable via a bare
  // `setPath(entity, "blockName", value)` with NO validation that the target block
  // DEFINITION exists — a rename to a non-existent block DANGLES the reference
  // (block-flatten `blockFromEntity` cannot resolve children → the insert renders
  // empty/broken) and exports a corrupt INSERT. That is an ADDITIVE first-time
  // write with a corruption risk and no advertised-safe path, so it is DEFERRED
  // (reverts to the EXACT prior no-op) — the same pattern as Hatch "Elevation" /
  // MText "Background fill". A dedicated block-rename slice must validate the
  // target-def existence (+ cascade the INSERT reference) before owning "Name".
  Block: new Set<string>(["Name"]),
};

/**
 * DEFUSE S15 Slice C (review BLOCK, P1 — prod schema-corruption) — point-coord
 * labels whose typed home is an OPTIONAL base point that can be ABSENT on a
 * VALID entity. EXCLUDED from the cutover owned set until a dedicated absent-base
 * CREATION slice.
 *
 * Routing them would let a single-axis edit of an absent optional base fabricate
 * a PARTIAL point: `setPath` on `extLine1Point.x` for a radial/diameter dimension
 * that carries NO `extLine1Point` (entityParser marks the extension points
 * optional), or on `alignmentPoint.x` for a default-justified TEXT with NO
 * `alignmentPoint`, writes `{ x: 5 }` and never seeds the sibling `y`.
 * `drawingPointSchema` (drawing-model model.ts) requires BOTH `x` and `y`, so the
 * entity turns schema-invalid: the dev/test `assembleDrawingDocumentInPlace`
 * re-validation THROWS, and PROD (which skips the O(N) walk and returns the entity
 * verbatim) SILENTLY persists the corrupt point → a broken DXF export.
 *
 * These labels were ADDITIVE silent-no-ops BEFORE Slice C (absent from
 * `labelToFieldByType` + `patchablePropertyLookup`, dropped by the translator's
 * generic tail), so excluding them REVERTS to that exact prior behaviour — no
 * regression, and the corruption can no longer be produced. ALWAYS-present base
 * points stay OWNED — Text / MText Insertion (`insertionPoint`, NON-optional in
 * the schema), Array `basePoint`, Point `location`, Ellipse `center` /
 * `majorAxisEndpoint` — because a single-axis edit clones a COMPLETE point by
 * construction (the base already carries both axes). The writer core additionally
 * carries a generic complete-point guard (`applyTypedPropertyEditWithDescriptor`)
 * so even a FUTURE optional-base coord left owned by mistake can never fabricate a
 * partial. FOLLOW-UP: a dedicated slice must seed the whole point from the
 * descriptor read fallback before these can be routed (absent-base creation).
 *
 * GRIPS-DRAG-MORE-TYPES (Codex P2, round 2) — Text "Alignment X"/"Alignment Y"
 * STAYS in this exclusion (a round-1 attempt to enable it directly was reverted).
 * The round-1 reasoning ("the generic complete-point guard makes a single-axis
 * write safe when the base is absent") is TRUE for the DOCUMENT writer, but the
 * SHELL-side mirror (`typedExtendedGeometryShellRejects`) only sees the flat
 * preview BAG (no live entity), so it cannot tell "alignmentPoint is genuinely
 * absent, falling back to insertionPoint's display" apart from "alignmentPoint is
 * present and happens to equal insertionPoint" — it would ACCEPT a bare
 * "Alignment X" edit on a default-justified TEXT (parses as a valid number) while
 * the document writer correctly REJECTS it, leaving the Properties panel showing
 * a value the DXF never persisted (the exact shell↔document divergence class this
 * whole exclusion-set mechanism exists to prevent). Instead, the Text insertion
 * grip drag's need to move `alignmentPoint` in lockstep with `insertionPoint` is
 * satisfied WITHOUT making "Alignment X"/"Alignment Y" independently
 * editable/owned at all: `descriptors/text.ts` routes Text's (not MText's —
 * MText has no `alignmentPoint`) "Insertion X"/"Insertion Y" through the
 * `textInsertionMoveMutator` SIDE-EFFECT (`sideEffectMutators.ts`), which shifts
 * `alignmentPoint` by the delta as a DERIVED consequence of the insertion move —
 * `alignmentPoint` is never itself a submitted label, so the shell-mirror gap
 * above can never be reached. "Alignment X"/"Alignment Y" therefore stay exactly
 * what they were BEFORE this entire GRIPS-DRAG-MORE-TYPES slice: an advertised
 * preview-bag row that is a documented silent no-op when edited directly — a
 * pre-existing gap this slice does not touch, tracked for the same dedicated
 * absent-base-creation slice as Dimension's Extension 1/2 X/Y below.
 */
const EXTENDED_GEOMETRY_OPTIONAL_BASE_POINT_COORDS: Record<
  string,
  ReadonlySet<string>
> = {
  Dimension: new Set<string>([
    "Extension 1 X",
    "Extension 1 Y",
    "Extension 2 X",
    "Extension 2 Y",
  ]),
  Text: new Set<string>(["Alignment X", "Alignment Y"]),
};

/**
 * DEFUSE S15 Slice C (@codex P2 ×2 on 21139696) — labels DEFERRED from the typed
 * extended-geometry cutover because owning them needs a SIDE-EFFECT mutator that
 * keeps TWO+ coupled fields in sync, NOT a single plain typed-field write.
 *
 * MText "Background fill" couples the UI boolean `backgroundFill` with the DXF
 * EXPORT field `backgroundFillSetting` — the writer that emits MTEXT group 90 is
 * driven by `backgroundFillSetting` (dxfBridge.entityConversion.ts line ~1357),
 * NOT by `backgroundFill`; the enabled fill also carries a colour
 * (`backgroundFillColorACI` / group 63) + `fillBoxScale` (group 45). So (P2-1) a
 * plain descriptor write to `backgroundFill` alone leaves `backgroundFillSetting`
 * STALE: the panel boolean flips while the exported fill state does not — a
 * shell↔export desync. Additionally (P2-2) the two preview factories DISAGREE on
 * this label's vocabulary: the apps/web IMPORT factory
 * (`dxfGeometry.buildMTextPreview`) emits "On"/"Off", while the command-engine
 * CREATE factory (`previewBuilders.deriveMtextPreviewProperties`) emits
 * "Yes"/"No" — yet the descriptor `toRaw` treats ONLY "on" as true, so a
 * create-path draft that says "Yes" parses as FALSE and turns the fill OFF.
 *
 * A correct cutover needs a side-effect mutator writing `backgroundFill` +
 * `backgroundFillSetting` (+ colour / scale) in lockstep AND a `toRaw` accepting
 * BOTH factory vocabularies — Slice E / a dedicated background-fill slice.
 * "Background fill" was an ADDITIVE silent no-op BEFORE Slice C (absent from
 * `labelToFieldByType`, dropped by the translator's generic tail; still an
 * advertised-editable preview-bag row), so excluding it here REVERTS to that
 * exact prior no-op — NO regression, and neither the export-desync nor the
 * vocabulary-parse-off can be produced. The both-vocabulary robustness gate that
 * fails a future additive boolean with this defect lives in the shell⊆writer
 * census (`extendedPropertyMutations.shellMirror.census.test.ts`).
 */
const EXTENDED_GEOMETRY_SIDE_EFFECT_DEFERRED: Record<string, ReadonlySet<string>> = {
  MText: new Set<string>(["Background fill"]),
};

/** Per-kind memo of the labels `buildTypedExtendedGeometryEdit` OWNS. */
const extendedGeometryOwnedLabelsByKind = new Map<EntityType, ReadonlySet<string>>();

/** Shared immutable empty set for kinds with no deferred-label overrides. */
const EMPTY_LABEL_SET: ReadonlySet<string> = new Set<string>();

/**
 * The entity-SPECIFIC labels the S15 extended-geometry builder OWNS for `kind` —
 * ONE TRUTH derived from the typed descriptor registry: every
 * `editable-typed-field` descriptor with a REAL plain typed home (non-empty
 * `typedFieldRef.path`, not read-only, not mutator-routed) that is NOT a common
 * object property (commons land via Step 1b/1c writers). Consumed by:
 *   - the builder's own resolve gate (never routes a common / read-only label),
 *   - the geometryReducer strip (these labels are REMOVED from the change set fed
 *     to the legacy translator — no double-write path),
 *   - the apps/web MATCHPROP persistability predicate
 *     (`isLabelDocumentPersistable`), which previously mirrored the deleted
 *     translator branches by hand.
 * Empty set for a kind outside {@link TYPED_EXTENDED_GEOMETRY_KINDS}.
 */
export function getTypedExtendedGeometryOwnedLabels(
  kind: EntityType,
): ReadonlySet<string> {
  if (!TYPED_EXTENDED_GEOMETRY_KINDS.has(kind)) return new Set();
  const memo = extendedGeometryOwnedLabelsByKind.get(kind);
  if (memo) return memo;
  const deferred = EXTENDED_GEOMETRY_CUTOVER_DEFERRED[kind] ?? EMPTY_LABEL_SET;
  const optionalBasePointCoords =
    EXTENDED_GEOMETRY_OPTIONAL_BASE_POINT_COORDS[kind] ?? EMPTY_LABEL_SET;
  const sideEffectDeferred =
    EXTENDED_GEOMETRY_SIDE_EFFECT_DEFERRED[kind] ?? EMPTY_LABEL_SET;
  const owned = new Set<string>();
  for (const d of getTypedPropertyDescriptors(kind)) {
    if (
      d.channel === "editable-typed-field" &&
      d.typedFieldRef !== undefined &&
      d.typedFieldRef.path !== "" &&
      // S15 Slice C — `enum` leaves stay on the legacy path until a dedicated
      // enum-label decoder subsystem: the `enumField` descriptor emits the RAW
      // integer code with no label↔code display/toRaw, so routing it would
      // show/write a raw int where AutoCAD shows a label — a ПРИОРИТЕТ #1
      // simplification. By-construction no-op for the shipped A/B kinds
      // (ArrayRect/LWPOLYLINE/Point/Ellipse carry zero `enum` descriptors); it
      // defers MText Attachment/Direction + every Multileader enum.
      d.editorKind !== "enum" &&
      // W2.1 (S16-ARRAYRECT-READONLY-ALIGN) — a STATIC `readOnly: true` (a
      // per-kind-always-read-only leaf, e.g. a future BEDIT-style field)
      // excludes the label from "owned" (nothing to route). A
      // FUTURE FUNCTION-valued (entity-STATE-dependent) `readOnly` would
      // instead stay owned: ownership is computed ONCE per KIND here (no
      // live entity in scope), so the per-entity accept/reject decision
      // would be deferred to the writer's own step-1 gate (`resolveReadOnly`
      // inside `applyTypedPropertyEditWithDescriptor`) — the same
      // "route through the typed writer, let it decide" shape a side-effect
      // cell already uses. `!== true` (not a bare truthy check) is the
      // by-construction reason a function reference never gets misread as
      // an always-excluded static flag. No registry descriptor currently
      // uses the function form (see `resolveReadOnly.test.ts` for direct
      // coverage of that branch on its own).
      d.constraints?.readOnly !== true &&
      !d.constraints?.sideEffects &&
      !isCommonObjectProperty(d.label) &&
      // S15 Slice C — a documented import-factory naming divergence (MText
      // "Line spacing" vs the bag's "Line spacing factor") that S16 unifies.
      !deferred.has(d.label) &&
      // S15 Slice C (@codex P2 ×2 on 21139696) — SIDE-EFFECT / two-field labels
      // (MText "Background fill" = backgroundFill boolean + backgroundFillSetting
      // export int, with the import/create factories on different vocabularies)
      // need a side-effect mutator, not a single-field write; deferred to Slice E.
      !sideEffectDeferred.has(d.label) &&
      // S15 Slice C review BLOCK (P1) — OPTIONAL-base point-coords (Dimension
      // Extension 1/2 X/Y, Text Alignment X/Y) are excluded: editing one axis of
      // an ABSENT optional base would persist a schema-invalid PARTIAL point.
      // Absent-base creation is a dedicated follow-up slice; see
      // EXTENDED_GEOMETRY_OPTIONAL_BASE_POINT_COORDS. (GRIPS-DRAG-MORE-TYPES —
      // Text's grip-drag needs alignmentPoint coupled to insertionPoint anyway;
      // that lives in the `textInsertionMoveMutator` SIDE-EFFECT instead of
      // making Alignment X/Y independently owned — see that exclusion set's doc.)
      !optionalBasePointCoords.has(d.label)
    ) {
      owned.add(d.label);
    }
  }
  extendedGeometryOwnedLabelsByKind.set(kind, owned);
  return owned;
}

/** Per-kind memo of the SIDE-EFFECT labels the mutator registry owns. */
const extendedGeometrySideEffectLabelsByKind = new Map<
  EntityType,
  ReadonlySet<string>
>();

/**
 * DEFUSE S15 Slice E — the entity-SPECIFIC labels routed through the side-effect
 * mutator registry for `kind` (the PIVOT's owned set, DISJOINT from
 * {@link getTypedExtendedGeometryOwnedLabels}). ONE TRUTH derived from the typed
 * descriptor registry: every `editable-typed-field` descriptor carrying a
 * `constraints.sideEffects` mutator that is REGISTERED (isSideEffectMutator-
 * Registered) and is not a common object property. A DECLARED-but-unregistered
 * mutator (viewport / block — Slice F/G) yields NO owned label here, so its
 * descriptor field is never advertised as writer-owned and the legacy path keeps
 * it (by-construction; a future registration flips it on with zero call-site
 * churn). For Hatch this is {Pattern} — the ONLY writable side-effect leaf; Solid
 * is READ-ONLY (a derived display of the SOLID-pattern state, @codex P2), not an
 * `editable-typed-field`, so it is excluded. Empty for the A/B/C kinds (no
 * side-effect descriptors) and for non-cutover kinds.
 *
 * Consumed by {@link buildTypedExtendedGeometrySideEffectPatch} (the reducer
 * Step), by {@link stripTypedExtendedGeometryChanges} (union with the plain owned
 * set so the legacy translator never double-writes a routed label), and by the
 * apps/web persistability predicate.
 */
export function getTypedExtendedGeometrySideEffectLabels(
  kind: EntityType,
): ReadonlySet<string> {
  if (!TYPED_EXTENDED_GEOMETRY_KINDS.has(kind)) return new Set();
  const memo = extendedGeometrySideEffectLabelsByKind.get(kind);
  if (memo) return memo;
  const owned = new Set<string>();
  for (const d of getTypedPropertyDescriptors(kind)) {
    const mutator = d.constraints?.sideEffects;
    if (
      d.channel === "editable-typed-field" &&
      d.typedFieldRef !== undefined &&
      mutator !== undefined &&
      isSideEffectMutatorRegistered(mutator) &&
      !isCommonObjectProperty(d.label)
    ) {
      owned.add(d.label);
    }
  }
  extendedGeometrySideEffectLabelsByKind.set(kind, owned);
  return owned;
}

/**
 * Immutable top-level DIFF of two entities into a `patchEntityById` shallow-merge
 * patch — the changed top-level fields (id/spaceId/type excluded). Used by the
 * side-effect Step: a mutator touches an UNKNOWN set of coupled fields (Pattern →
 * patternName + solidFill + gradient + customPatternLines), so the touched fields
 * cannot come from a single `typedFieldRef.path` — they are recovered by diffing
 * the mutated entity against the source. A CLEARED optional field (gradient →
 * `undefined`) survives as a `{ gradient: undefined }` cell so `patchEntityById`'s
 * spread + the schema re-parse REMOVE it. Returns `null` for an identity result.
 */
function diffTopLevelPatch(
  before: DrawingEntity,
  after: DrawingEntity,
): TypedFieldPatch | null {
  if (after === before) return null;
  const patch: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const key of keys) {
    if (key === "id" || key === "spaceId" || key === "type") continue;
    const b = (before as unknown as Record<string, unknown>)[key];
    const a = (after as unknown as Record<string, unknown>)[key];
    if (!Object.is(a, b)) patch[key] = a;
  }
  if (Object.keys(patch).length === 0) return null;
  return { entityId: before.id, changes: patch as Partial<DrawingEntity> };
}

/**
 * DEFUSE S15 Slice E — build the typed SIDE-EFFECT patch for a `properties/apply`
 * by SURFACING each writer side-effect directive
 * (`applyTypedPropertyEditWithDescriptor` → `status: "side-effect"`) and routing
 * it through the registered mutator (`applyTypedSideEffect`, which THROWS for an
 * unregistered name). Sibling of {@link buildTypedExtendedGeometryEdit} but for
 * the mutator-routed half: the touched coupled fields are recovered by diffing the
 * mutated entity against the source (the mutator rebuilds an UNKNOWN set of
 * fields, not a single path). `changes` MUST be the entity-SPECIFIC change set;
 * only owned side-effect labels ({@link getTypedExtendedGeometrySideEffectLabels})
 * are resolved. Returns `null` for a non-cutover kind / no owned side-effect
 * label / a pure no-op (e.g. an unchanged Pattern re-submit preserves the stored
 * gradient + precision).
 */
export function buildTypedExtendedGeometrySideEffectPatch(
  documentEntity: DrawingEntity,
  changes: ReadonlyArray<{ name: string; value: string }>,
  // S15 Slice F (@codex E — cross-step-dependency) — the PRE-mutation entity, when
  // it DIFFERS from `documentEntity`. `documentEntity` is the MUTATION BASE the
  // mutator DERIVES from (sourced by the reducer from the POST-Step-1d document, so
  // a coupled side-effect that depends on a PLAIN LEAF edited in the SAME apply —
  // Viewport `viewHeight` ← the plain `Height` leaf, `viewHeight = height × ratio`
  // — reads the ALREADY-applied value, not the stale pre-mutation one). The
  // CHANGED-sibling verdict + the changed-value NO-OP guard, by contrast, must stay
  // computed against the ORIGINAL field, so they read `preMutationBase`. Defaults
  // to `documentEntity` for a single-directive / test caller (the two coincide when
  // no plain leaf was applied first). Slice A–E callers pass one arg (identical).
  preMutationBase: DrawingEntity = documentEntity,
): TypedFieldPatch | null {
  const kind = documentEntity.type as EntityType;
  if (!TYPED_EXTENDED_GEOMETRY_KINDS.has(kind)) return null;
  const owned = getTypedExtendedGeometrySideEffectLabels(kind);
  if (owned.size === 0) return null;

  // CROSS-FIELD CHANGED-sibling verdict (S15 Slice D) — the labels of the OTHER
  // entity-specific changes in THIS apply whose write would GENUINELY change the
  // pre-mutation document (the writer's own `status === "applied"`, so a no-op /
  // reject full-bag row is excluded). A coupled-field mutator gates its
  // dependent-field rebuild on it (`splineFitRecomputeMutator` suppresses the
  // fit→control recompute when Control points / Knots were also GENUINELY edited —
  // the explicit control polygon wins). Computed against `preMutationBase` — the
  // genuine-change verdict is vs the ORIGINAL field, so a plain leaf that Step 1d
  // ALREADY applied to the mutation base still reads as "changed" here (else a
  // Control-points edit applied by Step 1d would look unchanged against the
  // post-plain base and wrongly stop suppressing the Spline recompute).
  const changedSiblingLabels = new Set<string>();
  for (const change of changes) {
    const d = getTypedPropertyDescriptor(kind, change.name);
    if (!d) continue;
    if (
      applyTypedPropertyEditWithDescriptor(preMutationBase, d, change.value).status ===
      "applied"
    ) {
      changedSiblingLabels.add(change.name);
    }
  }
  const context = { changedSiblingLabels };

  // The mutator DERIVES from `documentEntity` (the POST-Step-1d mutation base).
  let entity = documentEntity;
  for (const change of changes) {
    if (!owned.has(change.name)) continue;
    const descriptor = getTypedPropertyDescriptor(kind, change.name);
    if (!descriptor) continue;
    const result = applyTypedPropertyEditWithDescriptor(
      entity,
      descriptor,
      change.value,
    );
    // owned ⇒ a side-effect descriptor ⇒ the writer surfaces a directive; any
    // other status (a future non-side-effect owned label slipped in) is skipped.
    if (result.status !== "side-effect" || !result.sideEffect) continue;
    // GENERIC CHANGED-VALUE GUARD (@codex P1, data-integrity — protects the D/F/G
    // viewport/block side-effect arms too). Side-effect directives are surfaced
    // UNCONDITIONALLY (the writer core skips its step-5/6 no-op for them), so a
    // STALE UNCHANGED coupled row dragged in the full-bag single-select apply
    // (an imported hatch's `Solid="No"`, ordered AFTER `Pattern` — dxfGeometry
    // emits it; or a Viewport `Scale` re-dispatched unchanged while only `Height`
    // was edited) would OVERRIDE the derived state. Gate every directive with the
    // no-op predicate, compared against the PRE-MUTATION field (`preMutationBase`,
    // NOT the running `entity` a sibling directive may have already mutated NOR the
    // post-plain mutation base whose derived Scale display already shifted), so a
    // stale unchanged row is DROPPED while a GENUINE toggle still applies.
    //
    // @codex P2-4 / round-6 P2-1 — for a LABEL-CANONICAL mutator
    // (viewportScaleMutator) the descriptor RAW is a LOSSY ratio while the mutator
    // consumes the num:denom DISPLAY LABEL and writes the EXACT `vpOverrideScale` /
    // `viewportCannoscaleName` carriers, so the no-op verdict must compare the
    // submitted label against the STORED EXACT carrier — NOT the derived display
    // (which canonicalizes `2:200`→"1:100", falsely dropping a real relabel) nor the
    // generic `isTypedFieldEditNoop` (whose rawEqual backstop on the ratio would
    // false-noop). `labelCanonicalDirectiveIsNoop` owns that exact-carrier compare
    // (with a derived-display fallback for a no-carrier viewport). Every other
    // side-effect round-trips its raw faithfully → the generic guard.
    const directiveIsNoop = LABEL_CANONICAL_MUTATORS.has(result.sideEffect.mutator)
      ? labelCanonicalDirectiveIsNoop(
          result.sideEffect.mutator,
          preMutationBase,
          change.value,
          () => resolveDisplay(descriptor, preMutationBase),
        )
      : isTypedFieldEditNoop(
          descriptor,
          preMutationBase,
          change.value,
          result.sideEffect.raw,
        );
    if (directiveIsNoop) continue;
    entity = applyTypedSideEffect(entity, result.sideEffect, context);
  }
  return diffTopLevelPatch(documentEntity, entity);
}

/**
 * Build the typed EXTENDED-geometry patch for a Rectangular Associative Array /
 * LWPOLYLINE `properties/apply` by editing the LIVE document entity through the
 * typed writer — the S15 Slice A replacement for the two DELETED
 * `translateExtendedEntityPropertyChanges` per-kind branches. Thin sibling of
 * {@link buildTypedSimpleGeometryEdit} (same `buildTypedFieldPatch` core, same
 * geometryReducer Step shape): the per-kind geometry semantics live in the
 * registry descriptors (`arrayRectDescriptors` / `lwpolylineDescriptors`), which
 * already encode the deleted branches' validation matrix —
 *   - ArrayRect Rows/Columns: `int` round + `min:1` (an out-of-range count
 *     REJECTS per the writer's uniform contract — the S16 widget bound — instead
 *     of the legacy branch's silent `Math.max(1, …)` clamp fabricating a value);
 *   - Base X/Y: XY_PRESERVE point-coord — the untouched axis AND the group-30 Z
 *     keep the document's FULL raw precision by construction (the deleted
 *     branch needed `readRawPoint` to retrofit this);
 *   - LWPOLYLINE Vertices: count-locked strict-2D merge preserving per-vertex
 *     bulge / widths / vertexId (the deleted zip-with-document);
 *   - Bulges: strict all-or-nothing count-locked parse (`vertices[].bulge`
 *     array-map write — the Blocker-A regression cell);
 *   - Closed / Associative: the legacy truthy-token sets.
 *
 * `changes` MUST be the entity-SPECIFIC (non-common) changes — common props land
 * via Step 1b/1c; the resolve gate additionally hard-skips any label outside
 * {@link getTypedExtendedGeometryOwnedLabels}. Returns `null` for a non-cutover
 * kind OR when nothing changed (a no-op / all-rejected apply).
 */
export function buildTypedExtendedGeometryEdit(
  documentEntity: DrawingEntity,
  changes: ReadonlyArray<{ name: string; value: string }>,
): TypedFieldPatch | null {
  const kind = documentEntity.type as EntityType;
  if (!TYPED_EXTENDED_GEOMETRY_KINDS.has(kind)) return null;
  // @codex round-6 P2-2 — the paper-space CONTROL viewport (DXF viewportId=1) is
  // NEVER user-modifiable: the side-effect mutators already skip it
  // (`isControlViewport`, one truth in `viewportMutateEntity`), so the PLAIN leaves
  // (Center X/Y, Width, Height, View twist) must too. Without this, a `properties/
  // apply` / AI edit to a plain row could mutate the control viewport's frame even
  // though the render / print / dedicated `applyViewport*` command paths all treat
  // it as inert — ONE control-viewport rule across every viewport write path.
  if (kind === "Viewport" && isControlViewport(documentEntity as ViewportEntity)) {
    return null;
  }
  const owned = getTypedExtendedGeometryOwnedLabels(kind);
  return buildTypedFieldPatch(documentEntity, changes, (entity, change) =>
    owned.has(change.name)
      ? getTypedPropertyDescriptor(entity.type as EntityType, change.name)
      : undefined,
  );
}

/**
 * DEFUSE S15 Slice A (@codex P2 ×2 on c8394b70 — ONE class: shell↔document
 * divergence) — the SHELL mirror of the typed writer's accept/reject verdict
 * for a writer-OWNED extended-geometry label. Returns `true` when
 * {@link buildTypedExtendedGeometryEdit} would REJECT `value` for this
 * (kind, label): the shell preview applier must then SKIP the change, so the
 * string bag NEVER shows a value the typed document refused (ArrayRect
 * `Rows`/`Columns` = 0 / -2 / rounds-below-1 against `min:1+int`, LWPOLYLINE
 * `Constant width` = -1 against `min:0`, malformed serialized lists, …).
 *
 * ONE TRUTH by construction — nothing here is hand-mirrored:
 *   - the label set is {@link getTypedExtendedGeometryOwnedLabels} (the
 *     registry-derived ownership the geometryReducer Step 1d write uses), so
 *     every current AND future owned label (slices B/C/D/E …) inherits the
 *     mirror automatically;
 *   - the verdict is `parseTypedPropertyInput` — the writer's OWN steps 3–4
 *     (`toRaw` inverse + reject sentinel + numeric constraint), exported from
 *     the core rather than re-implemented.
 * Non-owned labels return `false` (the shell's existing validators keep owning
 * them); entity-context count locks (Bulges/Vertices) are NOT evaluated here
 * (the shell has no typed entity — parse-only, strictly more permissive), so
 * the shell's preview-derived count locks in
 * `isWellFormedCoordinateProperty` stay load-bearing for those. Pinned by the
 * shell⊆writer census (`extendedPropertyMutations.shellMirror.census.test.ts`).
 *
 * W2.1 (S16-ARRAYRECT-READONLY-ALIGN) — ALSO mirrors a STATIC `readOnly`
 * descriptor gate (ArrayRect `Associative`) for the SIDE-EFFECT-owned arm
 * below, so the shell preview never accepts+displays an edit the writer's
 * own step-1 gate rejects. The PLAIN-owned arm (`owned.has(label)`,
 * immediately below) does NOT need the same check for a STATIC
 * `readOnly: true` descriptor: that combination is already excluded from
 * {@link getTypedExtendedGeometryOwnedLabels}'s "owned" set at the source
 * (`d.constraints?.readOnly !== true`).
 *
 * N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2) — the first PLAIN-owned
 * descriptor with a FUNCTION-valued (entity-state-dependent) `readOnly`
 * (Hatch `Double`, gated on Pattern type). This function has NO live
 * `DrawingEntity` (the shell operates on the STRING preview bag), so it
 * cannot call the real `resolveReadOnly(descriptor, entity)` directly.
 * Instead it consults the descriptor's OPTIONAL `constraints.readOnlyFromBag`
 * — a shell-safe companion that re-derives the SAME verdict from sibling
 * PREVIEW BAG STRINGS (`bagProperties`, threaded in by the caller —
 * `applyExtendedEntityPropertyChanges`, which has the full `EntityPreview`
 * in scope). Returns `undefined` when the bag lacks the needed sibling key
 * (degrades to the pre-existing accept-and-let-the-writer-decide behaviour —
 * never a false reject on an incomplete bag); a descriptor with NO
 * `readOnlyFromBag` at all also degrades the same way (opt-in, not a new
 * universal requirement for every function-valued `readOnly`).
 *
 * @codex P2 round 5 — `allChanges` (OPTIONAL, the FULL raw apply batch) is
 * ALSO threaded through to `readOnlyFromBag`, so a descriptor can scan it
 * ORDER-INDEPENDENTLY (`Array.find`) instead of relying on `bagProperties`
 * alone. `bagProperties` on its own only ever reflects sibling changes the
 * shell's OWN accumulator loop has ALREADY applied before reaching THIS
 * label — order-DEPENDENT — while the document-side `readOnlyFromChanges`
 * (`buildTypedFieldPatch`) always scans the whole batch — order-
 * INDEPENDENT. Without `allChanges` here too, a batch listing the dependent
 * label BEFORE its sibling makes the SHELL decide against stale state while
 * the DOCUMENT decides against the batch's real final intent — a
 * shell↔document split caused by ordering ALONE, not by either side being
 * individually wrong.
 */
export function typedExtendedGeometryShellRejects(
  kind: string,
  label: string,
  value: string,
  bagProperties?: Readonly<Record<string, string>>,
  allChanges?: ReadonlyArray<{ name: string; value: string }>,
): boolean {
  const owned = getTypedExtendedGeometryOwnedLabels(kind as EntityType);
  if (owned.has(label)) {
    const descriptor = getTypedPropertyDescriptor(kind as EntityType, label);
    // owned ⇒ descriptor exists (ownership is derived FROM the registry); a
    // failed resolve is registry drift — fail CLOSED (skip the shell write)
    // rather than let the bag diverge from a document that cannot apply it.
    if (!descriptor) return true;
    if (
      typeof descriptor.constraints?.readOnly === "function" &&
      descriptor.constraints.readOnlyFromBag &&
      bagProperties !== undefined
    ) {
      const bagVerdict = descriptor.constraints.readOnlyFromBag(bagProperties, allChanges);
      if (bagVerdict === true) return true;
    }
    return parseTypedPropertyInput(descriptor, value).rejected;
  }
  // @codex P2-3 — ALSO mirror the SIDE-EFFECT owned labels' VALUE-level rejects
  // (Viewport Scale `0:N` / non-positive, View height ≤0), so the preview / audit
  // bag never accepts+displays a value the document MUTATOR refuses — the exact
  // shell↔document divergence this cutover removes, now covering the side-effect
  // arm too (not just plain owned labels). The verdict is the mutator's OWN
  // value-level validation (`sideEffectValueRejects`, one truth); Hatch/Spline
  // side-effects declare no value-reject (their empty/malformed inputs are
  // PRESERVE no-ops the shell's coordinate validators own) → false.
  const sideEffectOwned = getTypedExtendedGeometrySideEffectLabels(kind as EntityType);
  if (sideEffectOwned.has(label)) {
    const descriptor = getTypedPropertyDescriptor(kind as EntityType, label);
    const mutator = descriptor?.constraints?.sideEffects;
    if (!mutator) return false;
    // W2.1 — a STATICALLY read-only side-effect cell (ArrayRect `Associative`)
    // never reaches the mutator's own value-reject.
    if (descriptor && resolveReadOnly(descriptor, EMPTY_SHELL_PROBE)) {
      return true;
    }
    return sideEffectValueRejects(mutator, label, value);
  }
  return false;
}

/**
 * DEFUSE S15 Slice G (@codex P2 — canonicalizing-no-op display↔document desync) —
 * the SHELL mirror of the typed writer's CANONICALIZING no-op verdict for a
 * writer-OWNED extended-geometry label. Returns `true` when writing
 * `submittedValue` would be a NO-OP on the typed DOCUMENT (its parsed raw equals
 * the current display's parsed raw) YET the submitted display DIFFERS from the
 * current canonical display — the exact reported case: a MINSERT "Column count" of
 * "2.4" on a block already at 2 rounds to raw 2 (the `int` descriptor path), so
 * {@link buildTypedExtendedGeometryEdit}'s writer no-ops the document (Step 1d) but
 * the shell applier would otherwise store the literal "2.4". General to EVERY int /
 * canonicalizing owned field (ArrayRect `Rows`/`Columns`, future int cutovers).
 *
 * The shell applier must then KEEP the current canonical display rather than store
 * the raw submitted string: a stored "2.4" is a value the document / DXF export
 * never persist (the panel would show a phantom). Because the current display is
 * itself the canonical projection of the (unchanged) document value, keeping it IS
 * the canonical normalization — no typed entity needed here.
 *
 * ONE TRUTH — the verdict is `parseTypedPropertyInput` (the writer's own `toRaw`
 * inverse + int-round + numeric-constraint gate), the SAME function the document
 * writer and {@link typedExtendedGeometryShellRejects} run, so shell and document
 * agree by construction. Guards:
 *   - a display-EQUAL re-submit is already a no-op in the applier's own
 *     `nextProperties[label] === value` short-circuit, so this is only consulted
 *     for a DIFFERENT display string (belt-and-suspenders `!==` guard here too);
 *   - a REJECTED submit (empty / out-of-range) is NOT canonicalizing — it keeps
 *     its existing reject/preserve semantics (`typedExtendedGeometryShellRejects`);
 *   - a submit that parses to a DIFFERENT raw than the current display (incl. a
 *     full-precision re-type of a rounded float display) is NOT canonicalizing and
 *     stays on the store path — the value shown stays accurate either way (the
 *     document keeps its raw), so the #317/#418 precision-preserve no-op is never
 *     churned;
 *   - non-owned labels return `false` (the shell's own validators keep owning them).
 *
 * SIDE-EFFECT owned labels (Codex P2, local review round 7) — ALSO consulted
 * here, but gated per-mutator through {@link sideEffectCanonicalizes} (mirrors
 * `typedExtendedGeometryShellRejects`'s side-effect branch): a side-effect
 * mutator's no-op condition is not always plain raw-value equality of the
 * one submitted field (the coupled-field derivation can differ), so this is
 * a TRUE opt-in per mutator name, not a blanket extension of the plain-field
 * check. `arrayAssociativeMutator` (ArrayRect "Associative") is the first
 * opted-in case: its no-op condition IS exactly this raw-equality check.
 */
export function typedExtendedGeometryShellCanonicalizes(
  kind: string,
  label: string,
  submittedValue: string,
  currentDisplay: string | undefined,
): boolean {
  if (currentDisplay === undefined || currentDisplay === submittedValue) {
    return false;
  }
  const descriptor = getTypedPropertyDescriptor(kind as EntityType, label);
  if (!descriptor) return false;
  const owned = getTypedExtendedGeometryOwnedLabels(kind as EntityType);
  const isPlainOwned = owned.has(label);
  if (!isPlainOwned) {
    const sideEffectOwned = getTypedExtendedGeometrySideEffectLabels(kind as EntityType);
    if (!sideEffectOwned.has(label)) return false;
    const mutator = descriptor.constraints?.sideEffects;
    if (!mutator || !sideEffectCanonicalizes(mutator)) return false;
  }
  const submitted = parseTypedPropertyInput(descriptor, submittedValue);
  if (submitted.rejected) return false;
  const current = parseTypedPropertyInput(descriptor, currentDisplay);
  if (current.rejected) return false;
  return rawEqual(submitted.raw, current.raw);
}

/**
 * DEFUSE S15 Slice A — remove the extended-geometry labels the typed writer
 * ({@link buildTypedExtendedGeometryEdit}) now OWNS for this kind from a
 * `properties/apply` change set, so the legacy translator
 * (`translateExtendedEntityPropertyChanges`) can never double-write them. For the
 * cutover kinds the deleted branches leave the translator's generic tail with NO
 * mapping for these labels anyway (`labelToFieldByType` has no ArrayRect /
 * LWPOLYLINE entries), so this strip is the by-construction guarantee rather than
 * a behaviour change. Non-cutover kinds pass through untouched.
 */
export function stripTypedExtendedGeometryChanges<T extends { name: string }>(
  kind: string,
  changes: ReadonlyArray<T>,
): T[] {
  // S15 Slice E — strip BOTH the plain owned labels AND the side-effect owned
  // labels (Hatch Pattern / Solid), so the legacy translator can never
  // double-write a mutator-routed field (the deleted HATCH branch's `Pattern`
  // case is gone, and the still-legacy Hatch style / Pattern type cases are not
  // stripped — they are not owned by either set).
  const owned = getTypedExtendedGeometryOwnedLabels(kind as EntityType);
  const sideEffect = getTypedExtendedGeometrySideEffectLabels(kind as EntityType);
  if (owned.size === 0 && sideEffect.size === 0) return [...changes];
  return changes.filter(
    (change) => !owned.has(change.name) && !sideEffect.has(change.name),
  );
}
